import * as React from "react";
import { RouterLink as Link } from "../../router";
import { Card, CardContent } from "../../card";
import { Badge } from "../../badge";
import type {
  DbRelationshipDto,
  DbObjectDto,
} from "../../api/contracts/database/schema";

/**
 * Foreign-key relationship view. A force-free adjacency layout (PRD §5.4
 * sanctions this over a heavy graph library): each source table is a node,
 * its outgoing FK edges link to the referenced tables' schema detail. No new
 * graph package.
 */
export function SchemaGraph({
  relationships,
  objects,
}: {
  relationships: DbRelationshipDto[];
  objects: DbObjectDto[];
}) {
  const groupOf = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const o of objects) m.set(o.name, o.group);
    return m;
  }, [objects]);

  const bySource = React.useMemo(() => {
    const m = new Map<string, DbRelationshipDto[]>();
    for (const r of relationships) {
      const arr = m.get(r.from) ?? [];
      arr.push(r);
      m.set(r.from, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [relationships]);

  if (relationships.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No foreign-key relationships found.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="type-body text-muted-foreground">
        {relationships.length} foreign-key edge
        {relationships.length === 1 ? "" : "s"} across {bySource.length} table
        {bySource.length === 1 ? "" : "s"}.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {bySource.map(([from, edges]) => (
          <Card key={from}>
            <CardContent className="flex flex-col gap-2 py-4">
              <div className="flex items-center gap-2">
                <Link
                  href={`/database/schema/${encodeURIComponent(from)}`}
                  className="type-body text-foreground underline decoration-dotted underline-offset-2"
                >
                  {from}
                </Link>
                <Badge variant="outline" className="text-muted-foreground">
                  {groupOf.get(from) ?? "other"}
                </Badge>
              </div>
              <div className="flex flex-col gap-1 border-l border-border/40 pl-3">
                {edges.map((e, i) => (
                  <div key={i} className="flex items-center gap-1.5 type-subtext">
                    <span className="text-muted-foreground">
                      {e.columns.map((c) => c.from).join(", ")}
                    </span>
                    <span className="text-muted-foreground/60">→</span>
                    <Link
                      href={`/database/schema/${encodeURIComponent(e.to)}`}
                      className="text-foreground hover:underline"
                    >
                      {e.to}
                    </Link>
                    <span className="text-muted-foreground/60">
                      ({e.columns.map((c) => c.to).join(", ")})
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
